import React from 'react';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import {ActiveUserEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {ColorUtil} from '@mintdata-wsp/mint-kit/impl/util/ColorUtil';

const styles = require('./ActiveUserPointers.less');
const cx = classnames.bind(styles);

const PointerIcon = (props:any) => (
    <svg width="54" height="56" viewBox="0 0 54 56" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <g clipPath="url(#clip0_1_18)">
            <g filter="url(#filter0_d_1_18)">
                <path
                    d="M2.28728 2.60362L34.0762 21.8512L17.9836 25.0204L9.5016 39.0584L2.28728 2.60362Z"
                    fill={props.color}
                />
                <path
                    d="M9.88539 36.8764L3.42407 4.22716L31.8946 21.4654L17.8292 24.2356L17.482 24.3038L17.299 24.6068L9.88539 36.8764Z"
                    stroke="white" strokeWidth="2"
                />
            </g>
        </g>
        <defs>
            <filter
                id="filter0_d_1_18" x="-1.19695" y="0.597746" width="39.0185" height="43.8061"
                filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"
            >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                               result="hardAlpha" />
                <feOffset dy="1.6" />
                <feGaussianBlur stdDeviation="1.6" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_18" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_18" result="shape" />
            </filter>
            <clipPath id="clip0_1_18">
                <rect width="54" height="56" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

interface ActiveUserPointersProps
{
    ctx?:IWSPMeshContext;
    surface?:boolean;
    spreadsheet?:boolean;
}

@observer
export class ActiveUserPointers extends React.Component<ActiveUserPointersProps>
{
    private static readonly CAPTION_BACKGROUND_OPACITY = 0.7;

    public render():JSX.Element
    {
        const {surface, spreadsheet} = this.props;
        const {spreadsheets, openDesign} = controllers.ui;

        const {activeUsers} = rootState.ui.visualDesigner;

        if( !activeUsers.otherUsers )
            return null;

        let users = [];
        if( surface )
        {
            if( !openDesign.getDesignState().surface )
                return null;

            const activePage = openDesign.getActivePage();

            users = activeUsers.otherUsers
                .filter(user => user.cursorAt === 'surface' && user.page === activePage.id);
        }
        if( spreadsheet )
        {
            const activeSheet = spreadsheets.main.getActiveSheet();

            if( !activeSheet )
                return null;

            users = activeUsers.otherUsers
                .filter(user => user.cursorAt === 'spreadsheet' && user.sheet === activeSheet.id);
        }

        if( !users.length )
            return null;

        return <div>
            {
                users.map((user) =>
                {
                    const position = this._calculatePointerPosition(user);

                    if( Number.isNaN(position.pointerX) || Number.isNaN(position.pointerX) )
                        return null;

                    const userDetails = activeUsers.otherUsersDetails.get(user.userId);

                    if( !userDetails || userDetails.isIdle )
                        return null;

                    return (
                        <div key={user.userId}
                             className={cx('pointerContainer')}
                             style={{
                                 left: 0, top: 0,
                                 transform: `translate(${position.pointerX}px, ${position.pointerY}px)`
                             }}
                        >
                            <PointerIcon className={cx('pointer')}
                                         color={userDetails.color}
                            />

                            <span className={cx('caption')} style={{
                                backgroundColor: ColorUtil.RGBToRGBA(userDetails.color, ActiveUserPointers.CAPTION_BACKGROUND_OPACITY),
                                textShadow: `1px 1px 2px ${userDetails.color}`
                            }}>
                                {userDetails.fullName}
                            </span>
                        </div>
                    );
                })
            }
        </div>;
    }

    private _calculatePointerPosition(user:ActiveUserEvent)
    {
        const {mouseX, mouseY} = user;

        if( user.cursorAt === 'surface' )
        {
            const {x: panX, y: panY} = rootState.ui.visualDesigner.surfaceEditor.area.panModeState.position;
            const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;
            const {
                x: viewportX,
                y: viewportY
            } = controllers.ui.openDesign.getDesignState().surface.activePage.artboard.viewport.bounds;

            // -2 values are a slight shift caused by SVG shadow
            const pointerX = (viewportX + panX + mouseX) * zoom - 2;
            const pointerY = (viewportY + panY + mouseY) * zoom - 2;
            return {pointerX, pointerY};
        }
        else if( user.cursorAt === 'spreadsheet' )
        {
            const {mesh} = controllers.ui.spreadsheets;
            const meshState = mesh.getState(this.props.ctx);

            const {alignedScrollLeft, alignedScrollTop} = meshState;
            return {pointerX: mouseX - alignedScrollLeft, pointerY: mouseY - alignedScrollTop};
        }
        else
        {
            // no support of 'nestedframe' or other pointer areas yet
            return {};
        }
    }
}
